import { PartRecord } from "internal";
import SectionCollectionRecord from "models/view-models/section-collection-record";
import SectionRecord from "models/view-models/section-record";
import * as React from "react";
import { PropsWithChildren, useMemo, useState } from "react";
import SectionDetailWrapperWithinPanel from "organisms/publication-content/notebook-reference-panel/section-detail-wrapper-within-panel";
import { CollectionUtils } from "utilities/collection-utils";
import CodeReferenceActions from "pages/notebooks/notebook-reference-panel/bookview/code-reference-actions";
import { SectionDividerType } from "models/enumerations/section-divider-type";
import { SectionType } from "models/enumerations/section-type";
import SectionSpacer from "molecules/spacers/section-spacer";
import PartSeparatorWithinPanel from "./part-separator-within-panel";
import { XmlChangeNotationConstants } from "constants/xml-change-notation-constants";
import { NotebookPageContentBlock } from "constants/notebooks/notebook-page-content-blocks";
import { CodeReferenceBlockConfigurationRecord } from "models/view-models/block-configurations/code-reference-block-configuration-record";
import { usePageBlockState } from "utilities/hooks/domain/notebooks/pages/use-page-block-state";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface PublicationContentAreaWithinPanelProps {
    parts?: PartRecord[];
    sections: SectionRecord[];
    titleArea?: JSX.Element;

    // the external id of the parent component
    // optional becaue annex groups do not have an external id
    externalId?: string;
}

const CSS_CLASS_NAME = "c-publication-content-area-within-panel";

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const PublicationContentAreaWithinPanel: React.FunctionComponent<
    PropsWithChildren<PublicationContentAreaWithinPanelProps>
> = (props: PropsWithChildren<PublicationContentAreaWithinPanelProps>) => {
    const { sections, titleArea } = props;
    const [hoveredId, setHoveredId] = useState<number>();

    const handleOnSectionHover = (sectionId: number) => {
        setHoveredId(sectionId);
    };
    const sectionCollection = useMemo(
        () =>
            new SectionCollectionRecord({
                parts: props.parts ?? [],
                sections,
            }).buildSectionHierarchy(),
        [props.parts, sections]
    );

    return (
        <div className={CSS_CLASS_NAME}>
            {titleArea}
            {sectionCollection
                .filter(
                    (section) =>
                        section.changes !== XmlChangeNotationConstants.DELETION
                )
                .map((section: SectionRecord) => (
                    <>
                        <ParentSection
                            key={section.id}
                            section={section}
                            hoveredId={hoveredId}
                            onSectionHover={handleOnSectionHover}
                            sectionCollection={sectionCollection}
                        />
                        <SectionSpacer
                            type={getSpacerType(section, sectionCollection)}
                        />
                    </>
                ))}
        </div>
    );
};

// #endregion Component
const ChildrenSections = ({
    subSections,
    hoveredId,
    onSectionHover,
    sectionCollection,
}: {
    subSections: SectionRecord[];
    hoveredId?: number;
    onSectionHover: (id: number) => void;
    sectionCollection: SectionRecord[];
}) => {
    return (
        <div className={`${CSS_CLASS_NAME}__subsectionWrapper`}>
            {subSections.map((section) => (
                <ParentSection
                    key={section.id}
                    section={section}
                    hoveredId={hoveredId}
                    onSectionHover={onSectionHover}
                    sectionCollection={sectionCollection}
                />
            ))}
        </div>
    );
};
const ParentSection = ({
    section,
    hoveredId,
    onSectionHover,
    sectionCollection,
}: {
    section: SectionRecord;
    hoveredId?: number;
    onSectionHover: (id: number) => void;
    sectionCollection: SectionRecord[];
}) => {
    const { activeBlock } = usePageBlockState();
    const isCodeReferenceBlockEditorActiveButEmpty =
        activeBlock?.type === NotebookPageContentBlock.CodeReference &&
        !(activeBlock.configuration as CodeReferenceBlockConfigurationRecord)
            .sectionId;

    const part = section.displaySequence === 0 ? section.part : undefined;
    // Does not return deleted sections because we don't show change indicators/TIAs in panel
    if (section.changes === XmlChangeNotationConstants.DELETION) {
        return;
    }
    return (
        <>
            {part != null && (
                <PartSeparatorWithinPanel key={part.id} part={part}>
                    {part.getBody()}
                </PartSeparatorWithinPanel>
            )}
            <div
                className={`${CSS_CLASS_NAME}__sectionWrapper ${isCodeReferenceBlockEditorActiveButEmpty && section.id === hoveredId ? "-focused" : ""}`}>
                <SectionDetailWrapperWithinPanel
                    onSectionHover={onSectionHover}
                    key={section.id}
                    ref={(el) => {}}
                    section={section}>
                    {CollectionUtils.hasValues(section.subSections) && (
                        <ChildrenSections
                            subSections={section.subSections}
                            hoveredId={hoveredId}
                            onSectionHover={onSectionHover}
                            sectionCollection={sectionCollection}
                        />
                    )}
                </SectionDetailWrapperWithinPanel>
                {section.id === hoveredId &&
                    isCodeReferenceBlockEditorActiveButEmpty && (
                        <CodeReferenceActions section={section} />
                    )}
            </div>
        </>
    );
};

const getSpacerType = (
    currentSection: SectionRecord,
    sections: SectionRecord[]
): SectionDividerType => {
    const index = sections.indexOf(currentSection);
    const nextSection = sections[index + 1];

    // Pad the bottom of the document
    if (index === sections.length - 1) {
        return SectionDividerType.None;
    }

    // If next section is Level 0, add divider with rule
    if (nextSection.type === SectionType.Section) {
        if (nextSection.part != null) {
            return SectionDividerType.Small;
        }

        return SectionDividerType.LineOnly;
    }

    return SectionDividerType.None;
};
// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default React.memo(PublicationContentAreaWithinPanel);

// #endregion Exports
